<template>
  <!-- 기본 테이블 컴포넌트 - 재사용 가능한 테이블 구조 -->
  <div class="ui-table-wrapper-default">
    <table class="ui-table-default" :class="tableClasses">
      <!-- 컬럼 그룹 정의 -->
      <colgroup v-if="$slots.colgroup">
        <slot name="colgroup" />
      </colgroup>

      <!-- 테이블 헤더 -->
      <thead v-if="$slots.header" class="ui-table-default__header">
        <slot name="header" :selectAllProps="selectable ? selectAllProps : null" :hidden="hidden" />
      </thead>

      <!-- 테이블 바디 -->
      <tbody class="ui-table-default__body" :class="{ 'sortable-tbody': groupSortable }" ref="tableBody">
        <slot
          name="body"
          :rows="processedTableData"
          :loading="loading"
          :selectRowProps="selectable ? { isRowSelected, toggleRowSelection } : null"
          :onRowClick="handleRowClick"
          :hidden="hidden"
          :getCellSpan="getCellSpan"
          :shouldShowCell="shouldShowCell"
          :shouldShowGroupCell="shouldShowGroupCell"
          :getGroupRowspan="getGroupRowspan"
        />

        <!-- 로딩 상태 -->
        <tr v-if="loading" class="ui-table-default__loading">
          <td :colspan="columnCount" class="text-center">
            <div class="loading-spinner">로딩 중...</div>
          </td>
        </tr>

        <!-- 데이터 없음 상태 -->
        <!-- <tr v-else-if="!processedTableData?.length" class="ui-table-default__empty">
          <td :colspan="columnCount" class="text-center">
            {{ emptyMessage }}
          </td>
        </tr> -->
      </tbody>

      <!-- 테이블 푸터 -->
      <tfoot v-if="$slots.footer" class="ui-table-default__footer">
        <slot name="footer" />
      </tfoot>
    </table>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, nextTick, onBeforeUnmount } from 'vue';

/**
 * 테이블 컴포넌트의 Props 정의
 */
const props = defineProps({
  /**
   * 테이블 데이터 배열
   * @type {Array}
   */
  modelValue: {
    type: Array,
    default: () => [],
  },

  /**
   * 전체선택/개별선택 기능 활성화 여부
   * @type {Boolean}
   */
  selectable: {
    type: Boolean,
    default: false,
  },

  /**
   * 선택된 행들 (v-model:selected)
   * @type {Array}
   */
  selected: {
    type: Array,
    default: () => [],
  },

  /**
   * 행 식별 키 (기본: id)
   * @type {String}
   */
  rowKey: {
    type: String,
    default: 'id',
  },

  /**
   * 테이블 테두리 표시 여부
   * @type {Boolean}
   */
  bordered: {
    type: Boolean,
    default: false,
  },

  /**
   * 세로(열) 구분선 표시 여부
   * @type {Boolean}
   */
  verticalBorder: {
    type: Boolean,
    default: false,
  },

  /**
   * 줄무늬 스타일 적용 여부
   * @type {Boolean}
   */
  striped: {
    type: Boolean,
    default: false,
  },

  /**
   * 호버 효과 적용 여부
   * @type {Boolean}
   */
  hover: {
    type: Boolean,
    default: false,
  },

  /**
   * 테이블 크기 설정
   * @type {String} - 'small', 'medium', 'large'
   */
  size: {
    type: String,
    default: 'medium',
    validator: value => ['small', 'medium', 'large'].includes(value),
  },

  /**
   * 로딩 상태
   * @type {Boolean}
   */
  loading: {
    type: Boolean,
    default: false,
  },

  /**
   * 빈 데이터 시 표시할 메시지
   * @type {String}
   */
  emptyMessage: {
    type: String,
    default: '데이터가 없습니다.',
  },

  /**
   * 컬럼 수 (빈 상태, 로딩 상태 시 colspan 용)
   * @type {Number}
   */
  columnCount: {
    type: Number,
    default: 4,
  },

  /**
   * 세로형 테이블 레이아웃 여부
   * @type {Boolean}
   */
  vertical: {
    type: Boolean,
    default: false,
  },

  /**
   * 컬럼 숨김 여부
   * @type {Boolean}
   */
  hidden: {
    type: Boolean,
    default: false,
  },

  /**
   * HTML 템플릿 사용 여부
   * @type {Boolean}
   */
  htmlTemplate: {
    type: Boolean,
    default: false,
  },

  /**
   * 행 병합 설정
   * @type {Object}
   */
  rowSpanConfig: {
    type: Object,
    default: () => ({}),
  },

  /**
   * 열 병합 설정
   * @type {Object}
   */
  colspanConfig: {
    type: Object,
    default: () => ({}),
  },

  /**
   * 자동 rowspan 그룹핑 설정
   * 계층 구조 데이터를 자동으로 그룹핑하여 rowspan 적용
   * @type {Array} - 그룹핑할 컬럼 키 배열 (상위 그룹부터 순서대로)
   * 예: ['competencyName', 'category']
   */
  groupByColumns: {
    type: Array,
    default: () => [],
  },

  /**
   * 그룹핑 데이터의 중첩 구조 키
   * @type {String} - 하위 데이터 배열을 담고 있는 키명 (기본: 'items')
   */
  nestedDataKey: {
    type: String,
    default: 'items',
  },

  /**
   * 그룹 단위 드래그앤드롭 활성화 여부
   * @type {Boolean}
   */
  groupSortable: {
    type: Boolean,
    default: false,
  },

  /**
   * 드래그 핸들 선택자
   * @type {String}
   */
  sortHandle: {
    type: String,
    default: '.sort-handle',
  },
});

/**
 * 이벤트 정의
 */
const emit = defineEmits(['update:selected', 'row-click', 'group-sort']);

/**
 * 내부 선택 상태
 */
const internalSelected = ref([...props.selected]);

/**
 * rowKey 기준으로 선택 배열이 동일한지 비교 (부모↔자식 watch 순환 방지)
 */
const isSameRowSelection = (a, b, rowKey) => {
  const keysA = (Array.isArray(a) ? a : []).map(r => String(r?.[rowKey] ?? '')).sort();
  const keysB = (Array.isArray(b) ? b : []).map(r => String(r?.[rowKey] ?? '')).sort();
  if (keysA.length !== keysB.length) return false;
  return keysA.every((k, i) => k === keysB[i]);
};

/**
 * 선택 상태 동기화
 */
watch(
  () => props.selected,
  newVal => {
    if (isSameRowSelection(newVal, internalSelected.value, props.rowKey)) return;
    internalSelected.value = Array.isArray(newVal) ? [...newVal] : [];
  },
  { deep: true },
);

watch(
  internalSelected,
  newVal => {
    if (isSameRowSelection(newVal, props.selected, props.rowKey)) return;
    emit('update:selected', newVal);
  },
  { deep: true },
);

/**
 * 전체 선택 여부 확인
 */
const isAllSelected = computed(() => {
  if (!props.modelValue.length) return false;
  return props.modelValue.every(row => internalSelected.value.some(selected => selected[props.rowKey] === row[props.rowKey]));
});

/**
 * 부분 선택 여부 확인 (indeterminate)
 */
const isIndeterminate = computed(() => {
  const selectedCount = internalSelected.value.filter(selected => props.modelValue.some(row => row[props.rowKey] === selected[props.rowKey])).length;
  return selectedCount > 0 && selectedCount < props.modelValue.length;
});

/**
 * 전체 선택 / 해제 (UiCheckbox가 넘기는 checked 값 기준)
 */
const toggleSelectAll = checked => {
  const nextChecked = typeof checked === 'boolean' ? checked : !isAllSelected.value;

  if (nextChecked) {
    const currentPageKeys = props.modelValue.map(row => row[props.rowKey]);
    const otherSelected = internalSelected.value.filter(selected => !currentPageKeys.includes(selected[props.rowKey]));
    internalSelected.value = [...otherSelected, ...props.modelValue];
  } else {
    internalSelected.value = internalSelected.value.filter(selected => !props.modelValue.some(row => row[props.rowKey] === selected[props.rowKey]));
  }
};

/**
 * 개별 행 선택 여부 확인
 */
const isRowSelected = row => {
  return internalSelected.value.some(selected => selected[props.rowKey] === row[props.rowKey]);
};

/**
 * 개별 행 선택 토글
 */
const toggleRowSelection = row => {
  if (isRowSelected(row)) {
    internalSelected.value = internalSelected.value.filter(selected => selected[props.rowKey] !== row[props.rowKey]);
  } else {
    // push 대신 스프레드 연산자로 새로운 배열 생성
    internalSelected.value = [...internalSelected.value, row];
  }
};

/**
 * 전체선택 체크박스에 전달할 props
 */
const selectAllProps = computed(() => ({
  modelValue: isAllSelected.value,
  indeterminate: isIndeterminate.value,
  'onUpdate:modelValue': checked => toggleSelectAll(checked),
}));

/**
 * 행 클릭 핸들러
 * @param {Object} row - 클릭된 행 데이터
 * @param {Number} index - 행 인덱스
 * @param {Event} event - 클릭 이벤트
 */
const handleRowClick = (row, index, event) => {
  emit('row-click', { row, index, event });
};

/**
 * 테이블에 적용될 CSS 클래스 계산
 * @returns {Array} CSS 클래스 배열
 */
const tableClasses = computed(() => {
  return [
    `ui-table-default--${props.size}`,
    {
      'ui-table-default--bordered': props.bordered,
      'ui-table-default--vertical-border': props.verticalBorder,
      'ui-table-default--striped': props.striped,
      'ui-table-default--hover': props.hover,
      'ui-table-default--loading': props.loading,
      'ui-table-default--vertical': props.vertical,
      'ui-table-default--selectable': props.selectable,
    },
  ];
});

/**
 * 셀 span 계산 함수
 */
const getCellSpan = (row, rowIndex, columnKey) => {
  const result = { rowspan: 1, colspan: 1 };

  if (props.getRowSpan) {
    result.rowspan = props.getRowSpan(row, rowIndex, columnKey) || 1;
  }

  if (props.getColSpan) {
    result.colspan = props.getColSpan(row, rowIndex, columnKey) || 1;
  }

  return result;
};

/**
 * 셀 표시 여부 함수
 */
const shouldShowCell = (row, rowIndex, columnKey) => {
  if (!props.shouldShowCell) return true;
  return props.shouldShowCell(row, rowIndex, columnKey);
};

/**
 * 그룹핑된 테이블 데이터 계산
 * groupByColumns가 설정된 경우 자동으로 rowspan 처리
 */
const processedTableData = computed(() => {
  if (!props.groupByColumns.length) {
    return props.modelValue;
  }

  const flattened = [];

  props.modelValue.forEach(group => {
    // 중첩 구조인 경우 (예: competencyData)
    if (group[props.nestedDataKey] && Array.isArray(group[props.nestedDataKey])) {
      processNestedData(group, flattened);
    } else {
      // 평면 구조인 경우
      flattened.push(group);
    }
  });

  return addRowspanInfo(flattened);
});

/**
 * 중첩된 데이터 구조 처리
 * @param {Object} group - 그룹 데이터
 * @param {Array} flattened - 결과 배열
 */
const processNestedData = (group, flattened) => {
  const items = group[props.nestedDataKey];

  // 그룹 컬럼이 2개 이상인 경우 (예: competencyName, category)
  if (props.groupByColumns.length >= 2) {
    const [firstGroupKey, secondGroupKey] = props.groupByColumns;

    // 두 번째 그룹핑 키로 하위 그룹 생성
    const subGroups = {};
    items.forEach(item => {
      const subGroupValue = item[secondGroupKey];
      if (!subGroups[subGroupValue]) {
        subGroups[subGroupValue] = [];
      }
      subGroups[subGroupValue].push(item);
    });

    // 각 서브그룹의 첫 번째 항목에 정보 추가
    let isFirstInMainGroup = true;

    Object.entries(subGroups).forEach(([subGroupValue, subItems]) => {
      subItems.forEach((item, subIndex) => {
        flattened.push({
          ...item,
          [firstGroupKey]: group[firstGroupKey],
          // 첫 번째 그룹 rowspan 정보
          [`is${firstGroupKey}First`]: isFirstInMainGroup,
          [`${firstGroupKey}Rowspan`]: items.length,
          // 두 번째 그룹 rowspan 정보
          [`is${secondGroupKey}First`]: subIndex === 0,
          [`${secondGroupKey}Rowspan`]: subItems.length,
          [`${secondGroupKey}Value`]: subGroupValue,
        });

        if (isFirstInMainGroup) isFirstInMainGroup = false;
      });
    });
  } else {
    // 단일 그룹핑인 경우
    const groupKey = props.groupByColumns[0];
    items.forEach((item, index) => {
      flattened.push({
        ...item,
        [groupKey]: group[groupKey],
        [`is${groupKey}First`]: index === 0,
        [`${groupKey}Rowspan`]: items.length,
      });
    });
  }
};

/**
 * 평면 데이터에 rowspan 정보 추가
 * @param {Array} data - 평면 데이터 배열
 * @returns {Array} rowspan 정보가 추가된 데이터
 */
const addRowspanInfo = data => {
  if (!props.groupByColumns.length) return data;

  // 그룹핑된 데이터에서 rowspan 계산
  const result = [];
  const groupCounts = {};

  // 각 그룹의 개수 계산
  data.forEach(item => {
    props.groupByColumns.forEach(groupKey => {
      const value = item[groupKey];
      if (!groupCounts[groupKey]) groupCounts[groupKey] = {};
      groupCounts[groupKey][value] = (groupCounts[groupKey][value] || 0) + 1;
    });
  });

  // rowspan 정보 추가
  const processed = {};
  data.forEach(item => {
    const processedItem = { ...item };

    props.groupByColumns.forEach(groupKey => {
      const value = item[groupKey];
      const key = `${groupKey}_${value}`;

      if (!processed[key]) {
        processedItem[`is${groupKey}First`] = true;
        processedItem[`${groupKey}Rowspan`] = groupCounts[groupKey][value];
        processed[key] = true;
      } else {
        processedItem[`is${groupKey}First`] = false;
        processedItem[`${groupKey}Rowspan`] = 0;
      }
    });

    result.push(processedItem);
  });

  return result;
};

/**
 * 셀 표시 여부 확인 (rowspan 적용 시)
 * @param {Object} row - 행 데이터
 * @param {String} columnKey - 컬럼 키
 * @returns {Boolean} 셀 표시 여부
 */
const shouldShowGroupCell = (row, columnKey) => {
  if (!props.groupByColumns.includes(columnKey)) return true;
  return row[`is${columnKey}First`];
};

/**
 * 셀의 rowspan 값 계산
 * @param {Object} row - 행 데이터
 * @param {String} columnKey - 컬럼 키
 * @returns {Number} rowspan 값
 */
const getGroupRowspan = (row, columnKey) => {
  if (!props.groupByColumns.includes(columnKey)) return 1;
  return row[`${columnKey}Rowspan`] || 1;
};

/**
 * Group Dragging 시스템
 */

// Sortable 그룹 정렬 관련 참조와 변수
const tableBody = ref(null);
let sortableInstance = null;
let draggingGroupId = '';
let lastMoveSignature = '';
let stagedGroupOrder = [];
let startGroupOrder = [];
let hasManualGroupMove = false;
const GROUP_SWAP_ANIMATION_MS = 180;
const GROUP_SWAP_COOLDOWN_MS = 180;
let lastGroupSwapAt = 0;

/**
 * 현재 그룹 순서 계산 (DOM 기반)
 */
const calculateCurrentGroupOrder = tbody => {
  const allRows = Array.from(tbody.children).filter(row => row.tagName === 'TR' && row.getAttribute('data-group-id'));

  const groupOrder = [];

  allRows.forEach(row => {
    const groupId = row.getAttribute('data-group-id');
    const isFirst = row.getAttribute('data-group-first') === 'true';

    if (groupId && isFirst && !groupOrder.includes(groupId)) {
      groupOrder.push(groupId);
    }
  });

  return groupOrder;
};

const destroySortable = () => {
  sortableInstance?.destroy?.();
  sortableInstance = null;
};

const normalizeGroupedRows = tbody => {
  if (!tbody) return;

  const firstRows = Array.from(tbody.querySelectorAll('tr[data-group-first="true"][data-group-id]'));
  if (!firstRows.length) return;

  const groupOrder = firstRows.map(row => row.getAttribute('data-group-id')).filter(Boolean);
  if (!groupOrder.length) return;

  // 현재 DOM에서 그룹별 행 목록(분리되어 있어도 포함)을 먼저 수집
  const groupedRowMap = new Map();
  groupOrder.forEach(groupId => {
    groupedRowMap.set(groupId, Array.from(tbody.querySelectorAll(`tr[data-group-id="${groupId}"]`)));
  });

  const fragment = document.createDocumentFragment();
  groupOrder.forEach(groupId => {
    const rows = groupedRowMap.get(groupId) || [];
    rows.forEach(row => fragment.appendChild(row));
  });

  tbody.appendChild(fragment);
};

const applyGroupOrderToDom = (tbody, groupOrder) => {
  if (!tbody || !Array.isArray(groupOrder) || !groupOrder.length) return;

  const previousRects = new WeakMap();
  const allGroupRows = Array.from(tbody.querySelectorAll('tr[data-group-id]'));
  allGroupRows.forEach(row => {
    previousRects.set(row, row.getBoundingClientRect());
  });

  const groupedRowMap = new Map();
  allGroupRows.forEach(row => {
    const gid = row.getAttribute('data-group-id');
    if (!gid) return;
    if (!groupedRowMap.has(gid)) groupedRowMap.set(gid, []);
    groupedRowMap.get(gid).push(row);
  });

  const safeOrder = [...groupOrder];
  // order에 누락된 그룹이 있으면 뒤에 보존 추가 (행 유실 방지)
  Array.from(groupedRowMap.keys()).forEach(gid => {
    if (!safeOrder.includes(gid)) safeOrder.push(gid);
  });

  const fragment = document.createDocumentFragment();
  safeOrder.forEach(groupId => {
    const rows = groupedRowMap.get(groupId) || [];
    rows.forEach(row => fragment.appendChild(row));
  });

  tbody.appendChild(fragment);

  // FLIP: 재배치 전/후 위치 차이를 이용해 부드러운 그룹 교체 애니메이션을 준다.
  requestAnimationFrame(() => {
    const movedRows = Array.from(tbody.querySelectorAll('tr[data-group-id]'));
    movedRows.forEach(row => {
      const prevRect = previousRects.get(row);
      if (!prevRect) return;

      const nextRect = row.getBoundingClientRect();
      const deltaY = prevRect.top - nextRect.top;
      if (Math.abs(deltaY) < 1) return;

      row.animate([{ transform: `translateY(${deltaY}px)` }, { transform: 'translateY(0)' }], {
        duration: GROUP_SWAP_ANIMATION_MS,
        easing: 'ease-out',
      });
    });
  });
};

const finalizeGroupSort = (tbody, finalizedOrder) => {
  if (!tbody) return;
  const domOrder = calculateCurrentGroupOrder(tbody);
  const mergedOrder = [];
  (Array.isArray(finalizedOrder) ? finalizedOrder : []).forEach(id => {
    if (id && !mergedOrder.includes(id)) mergedOrder.push(id);
  });
  domOrder.forEach(id => {
    if (id && !mergedOrder.includes(id)) mergedOrder.push(id);
  });

  if (mergedOrder.length) {
    applyGroupOrderToDom(tbody, mergedOrder);
    normalizeGroupedRows(tbody);
    emit('group-sort', mergedOrder);
    return;
  }

  const fallbackOrder = calculateCurrentGroupOrder(tbody);
  if (fallbackOrder?.length) {
    applyGroupOrderToDom(tbody, fallbackOrder);
    normalizeGroupedRows(tbody);
    emit('group-sort', fallbackOrder);
  }
};

const computeMovedOrder = (currentOrder, movingGroupId, targetGroupId, insertAfter) => {
  if (!Array.isArray(currentOrder) || !movingGroupId || !targetGroupId) return currentOrder;
  if (movingGroupId === targetGroupId) return currentOrder;

  const fromIndex = currentOrder.indexOf(movingGroupId);
  const targetIndex = currentOrder.indexOf(targetGroupId);
  if (fromIndex === -1 || targetIndex === -1) return currentOrder;

  const nextOrder = [...currentOrder];
  nextOrder.splice(fromIndex, 1);
  const adjustedTargetIndex = fromIndex < targetIndex ? targetIndex - 1 : targetIndex;
  const insertIndex = insertAfter ? adjustedTargetIndex + 1 : adjustedTargetIndex;
  nextOrder.splice(insertIndex, 0, movingGroupId);

  return nextOrder;
};

/**
 * 그룹 드래그 시스템 초기화
 */
const initVirtualGroupDrag = () => {
  if (!props.groupSortable) return;
  if (!import.meta.client) return;

  nextTick(() => {
    const tbody = tableBody.value;
    if (!tbody) return;

    destroySortable();

    const { $Sortable } = useNuxtApp();
    sortableInstance = new $Sortable(tbody, {
      handle: props.sortHandle,
      animation: 150,
      draggable: 'tr[data-group-first="true"]',
      onStart: evt => {
        draggingGroupId = evt?.item?.getAttribute?.('data-group-id') || '';
        lastMoveSignature = '';
        startGroupOrder = calculateCurrentGroupOrder(tbody);
        stagedGroupOrder = [...startGroupOrder];
        hasManualGroupMove = false;
        lastGroupSwapAt = 0;
      },
      onMove: evt => {
        const now = performance.now();
        if (now - lastGroupSwapAt < GROUP_SWAP_COOLDOWN_MS) {
          return false;
        }

        const movingGroupId = evt?.dragged?.getAttribute?.('data-group-id') || draggingGroupId;
        const relatedRow = evt?.related;
        const targetGroupId = relatedRow?.getAttribute?.('data-group-id');
        if (!movingGroupId || !targetGroupId || movingGroupId === targetGroupId) {
          return false;
        }

        const insertAfter = !!evt?.willInsertAfter;
        const moveSignature = `${movingGroupId}->${targetGroupId}:${insertAfter ? 'after' : 'before'}`;
        if (moveSignature !== lastMoveSignature) {
          stagedGroupOrder = computeMovedOrder(stagedGroupOrder, movingGroupId, targetGroupId, insertAfter);
          applyGroupOrderToDom(tbody, stagedGroupOrder);
          normalizeGroupedRows(tbody);
          lastMoveSignature = moveSignature;
          hasManualGroupMove = true;
          lastGroupSwapAt = now;
        }

        // 기본 first-row 단일 이동을 차단하고 그룹 블록 이동만 허용
        return false;
      },
      onEnd: evt => {
        const draggedGroupId = evt?.item?.getAttribute?.('data-group-id') || draggingGroupId;
        const oldIndex = Number.isInteger(evt?.oldDraggableIndex) ? evt.oldDraggableIndex : null;
        const newIndex = Number.isInteger(evt?.newDraggableIndex) ? evt.newDraggableIndex : null;
        let finalizedOrder = stagedGroupOrder?.length ? [...stagedGroupOrder] : calculateCurrentGroupOrder(tbody);

        // 드래그 중 수동 블록 이동이 없었던 경우에만 인덱스 기반 최종 순서 계산
        if (!hasManualGroupMove && oldIndex != null && newIndex != null && startGroupOrder.length > 0) {
          const ordered = [...startGroupOrder];
          const [moved] = ordered.splice(oldIndex, 1);
          if (moved != null) {
            ordered.splice(newIndex, 0, moved);
            finalizedOrder = ordered;
          }
        }

        // Sortable가 드롭 DOM 정리를 끝낸 다음(다음 프레임) 순서를 확정한다.
        requestAnimationFrame(() => {
          if (draggedGroupId) {
            finalizeGroupSort(tbody, finalizedOrder);
          } else {
            finalizeGroupSort(tbody, []);
          }
        });
        draggingGroupId = '';
        lastMoveSignature = '';
        stagedGroupOrder = [];
        startGroupOrder = [];
        hasManualGroupMove = false;
        lastGroupSwapAt = 0;
      },
    });
  });
};

/**
 * 컴포넌트 라이프사이클
 */
onMounted(() => {
  nextTick(() => {
    initVirtualGroupDrag();
  });
});

onBeforeUnmount(() => {
  destroySortable();
});
</script>

<style lang="scss" scoped>
/**
 * UiTableDefault 컴포넌트 스타일
 * 가로 스크롤 지원 및 반응형 테이블 구현
 */

/* 테이블 래퍼 - 가로 스크롤 컨테이너 */
.ui-table-wrapper-default {
  width: 100%;
  overflow-x: auto; /* 가로 스크롤 활성화 */
  overflow-y: visible;

  /* 커스텀 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;

    &:hover {
      background: #a8a8a8;
    }
  }

  /* Firefox 스크롤바 스타일링 */
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
}

/* 기본 테이블 스타일 */
.ui-table-default {
  width: 100%;
  border-collapse: separate;
  table-layout: auto;

  /* 테두리가 있는 테이블 */
  &.ui-table-default--bordered {
    border: 1px solid #e0e0e0;

    :deep(th),
    :deep(td) {
      border: 1px solid #e0e0e0;
    }
  }

  /* 세로(열) 구분선 — 슬롯 th/td에도 적용되도록 :deep 사용 */
  &.ui-table-default--vertical-border {
    :deep(th),
    :deep(td) {
      border-left: 1px solid #e0e0e0;
    }

    :deep(tr > th:first-child),
    :deep(tr > td:first-child) {
      border-left: none;
    }
  }

  /* 선택 가능한 테이블 */
  &.ui-table-default--selectable {
    tbody tr {
      cursor: pointer;
      transition: background-color 0.2s ease;

      &:hover {
        background-color: #f5f5f5;
      }

      &.selected-row {
        background-color: #e3f2fd;
      }
    }
  }

  /* 세로형 테이블 레이아웃 */
  &.ui-table-default--vertical {
    border-collapse: collapse;
    th,
    td {
      display: block;
      width: 100%;
      text-align: left;

      &:first-child {
        font-weight: bold;
        background-color: #f8f9fa;
      }
    }

    thead,
    tbody,
    tfoot {
      display: block;
    }

    tr {
      display: block;
      margin-bottom: 1rem;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
    }
  }
}

/* 테이블 헤더 */
.ui-table-default__header {
  background-color: #f8f9fa;

  th {
    padding: 12px 16px;
    font-weight: 600;
    text-align: left;
    color: #333;
    white-space: nowrap; /* 헤더 텍스트 줄바꿈 방지 */

    &:first-child {
      border-top-left-radius: 4px;
    }

    &:last-child {
      border-top-right-radius: 4px;
    }
  }
}

/* 테이블 바디 — 슬롯으로 들어오는 tr/td에도 적용되도록 :deep 사용 */
.ui-table-default__body {
  :deep(td) {
    &.wrap-text {
      white-space: normal;
      overflow-wrap: anywhere;
      word-break: break-word;
      min-width: 0;
    }
  }

  tr {
    &:nth-child(even) {
      background-color: #fafafa;
    }

    &:hover {
      background-color: #f0f0f0;
    }
  }
}

/* 테이블 푸터 */
.ui-table-default__footer {
  background-color: #f8f9fa;

  td {
    padding: 12px 16px;
    font-weight: 500;
    color: #333;

    &:first-child {
      border-bottom-left-radius: 4px;
    }

    &:last-child {
      border-bottom-right-radius: 4px;
    }
  }
}

/* 로딩 상태 */
.ui-table-default__loading {
  td {
    padding: 40px 16px;
    text-align: center;
    color: #999;

    .loading-spinner {
      display: inline-flex;
      align-items: center;
      gap: 8px;

      &::before {
        content: '';
        width: 16px;
        height: 16px;
        border: 2px solid #e0e0e0;
        border-top: 2px solid #007bff;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }
    }
  }
}

/* 빈 상태 */
.ui-table-default__empty {
  td {
    padding: 40px 16px;
    text-align: center;
    color: #999;
    font-style: italic;
  }
}

/* 스피너 애니메이션 */
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* 그룹 드래그 관련 스타일 */
.sortable-tbody {
  .group-dragging {
    opacity: 0.6;
    background-color: #f0f9ff; /* 밝은 파란 배경 */
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15); /* 그림자 효과 */
    transition: all 0.2s ease;
  }

  &.group-reorder-animate {
    :deep(tr[data-group-id]) {
      animation: groupReorderFlash 0.26s ease;
    }
  }

  .drop-target-before {
    box-shadow: inset 0 2px 0 #3b82f6;
  }

  .drop-target-after {
    box-shadow: inset 0 -2px 0 #3b82f6;
  }
}

@keyframes groupReorderFlash {
  0% {
    background-color: #eaf5ff;
  }
  100% {
    background-color: transparent;
  }
}

/* 반응형 대응 */
@media (max-width: 768px) {
  .ui-table-wrapper-default {
    /* 모바일에서는 스크롤바를 더 얇게 */
    &::-webkit-scrollbar {
      height: 6px;
    }
  }

  .ui-table-default {
    th,
    td {
      padding: 8px 12px;
      font-size: 14px;
    }
  }
}
</style>
